import { MarkerType, type Edge, type Node } from '@xyflow/react';
import type { Diagnostic, Diagram, DiagramNode } from '@civil/schema';
import type { Descent, NodeData } from './nodes.js';
import { workingSet, type CodeSources } from './working-set.js';

/**
 * A flow or architecture diagram (docs/interpretation.md) as React Flow draws it.
 * Nodes are roles or technical pieces, not composition parts; a node with `ref` is a
 * door into another diagram of the same view. Edges say what passes. Nothing here is
 * executed, so a loop is drawn like any other line.
 *
 * A Story diagram (docs/altitude.md) is drawn as a journey strip, not a node graph:
 * its moments sit on one band in the order a person meets them, each listing what it
 * covers at Product. Going down is the altitude switch, so a moment is not a door.
 *
 * Each face says what opening it does (docs/altitude.md §7): a moment is a story card;
 * a door says how much it opens; and a flow step with nothing beneath it but files is
 * the code face, released into the editor on its working set — PRD §2's floor.
 */

/** What the canvas and inspector know about a diagram node. */
export interface RoleItem {
  id: string;
  type: 'role';
  kind: string;
  name: string;
  view: string;
  diagram: string;
  /** The diagram's file. */
  path: string;
  node: DiagramNode;
}

const FALLBACK = { x: 60, y: 60 };

/** The band a story's moments sit on: its id is never a node's (ids take no underscore). */
export const BAND_ID = '__story_band';
const BAND_PAD = { x: 36, top: 56, bottom: 32 };
const CARD = { w: 300, h: 176 };

/** "Today, Hub · People": a cover's nodes by name, grouped as their diagrams hold them. */
function coverNames(node: DiagramNode, diagrams: Record<string, { doc: Diagram }> | undefined): string[] {
  return node.covers.map((c) => {
    const doc = diagrams?.[c.diagram]?.doc;
    const names = c.nodes.map((id) => doc?.spec.nodes.find((n) => n.id === id)?.name ?? id);
    return names.join(', ');
  });
}

/**
 * Each moment's place in the strip, read from where it is drawn (the author may have
 * moved it): who comes, then "1 of 5" onward, then what they get.
 */
function storyOrder(diagram: Diagram): Map<string, string> {
  const at = (id: string) => diagram.layout.nodes[id]?.x ?? 0;
  const moments = diagram.spec.nodes.filter((n) => n.kind !== 'actor' && n.kind !== 'output').sort((a, b) => at(a.id) - at(b.id));
  const order = new Map<string, string>();
  for (const n of diagram.spec.nodes) {
    if (n.kind === 'actor') order.set(n.id, 'Who');
    if (n.kind === 'output') order.set(n.id, 'What they get');
  }
  moments.forEach((n, i) => order.set(n.id, `${i + 1} of ${moments.length}`));
  return order;
}

export function diagramToFlow(
  diagram: Diagram,
  path: string,
  view: string,
  diagnostics: Diagnostic[],
  /** The view's diagrams, so a cover can be named; absent, it is named by id. */
  diagrams?: Record<string, { doc: Diagram }>,
  /** Where a flow step's files are read from; absent, no step opens the editor. */
  code?: CodeSources,
): { nodes: Node<NodeData>[]; edges: Edge[] } {
  const story = diagram.spec.altitude === 'story';
  const layout = diagram.layout.nodes;
  const sequence = story ? storyOrder(diagram) : new Map<string, string>();
  // The floor: a flow's working steps (Mechanism, or a flow written before altitudes).
  const floor = diagram.spec.altitude === 'mechanism' || (!diagram.spec.altitude && code?.loaded.view.spec.shape === 'flow');
  const nodes = diagram.spec.nodes.map((n, index): Node<NodeData> => {
    const item: RoleItem = { id: n.id, type: 'role', kind: n.kind, name: n.name, view, diagram: diagram.metadata.id, path, node: n };
    const decisions = n.decisions.length;
    const opens = n.ref ? diagrams?.[n.ref]?.doc.spec.nodes.length : undefined;
    let descent: Descent = n.ref ? { into: 'canvas', ports: [], summary: opens ? `opens ${opens}` : 'step in' } : undefined;
    if (!descent && floor && code) {
      const groups = workingSet(code, diagram.metadata.id, [n.id]);
      if (groups.length) descent = { into: 'code', files: groups.flatMap((g) => g.files), note: 'working set', groups };
    }
    return {
      id: n.id,
      type: 'role',
      position: layout[n.id] ?? { x: FALLBACK.x + index * 220, y: FALLBACK.y + (index % 3) * 120 },
      data: {
        label: n.name,
        descent,
        ...(opens ? { opens } : {}),
        detail: n.summary,
        contract: undefined,
        contractError: undefined,
        diagnostics: diagnostics.filter((d) => d.file === path && d.nodeId === n.id),
        manifest: item,
        ...(n.confidence ? { confidence: n.confidence } : {}),
        ...(decisions ? { badge: `${decisions} decision${decisions === 1 ? '' : 's'}` } : {}),
        ...(story ? { moment: true, covers: coverNames(n, diagrams), sequence: sequence.get(n.id) ?? '' } : {}),
      },
    };
  });
  if (story && nodes.length) {
    // One band under the whole journey, wherever the author has moved its moments.
    const xs = nodes.map((n) => n.position.x);
    const ys = nodes.map((n) => n.position.y);
    const left = Math.min(...xs) - BAND_PAD.x;
    const top = Math.min(...ys) - BAND_PAD.top;
    nodes.unshift({
      id: BAND_ID,
      type: 'band',
      position: { x: left, y: top },
      draggable: false,
      selectable: false,
      focusable: false,
      zIndex: -1,
      style: { width: Math.max(...xs) + CARD.w + BAND_PAD.x - left, height: Math.max(...ys) + CARD.h + BAND_PAD.bottom - top },
      data: {
        label: diagram.metadata.name ?? 'The story',
        descent: undefined,
        detail: 'In the order a person meets it. Each moment covers sections one altitude down.',
        contract: undefined,
        contractError: undefined,
        diagnostics: [],
        manifest: { id: BAND_ID, type: 'role', kind: 'band', name: '', view, diagram: diagram.metadata.id, path, node: { id: BAND_ID, kind: 'band', name: '', realizedBy: [], evidence: [], decisions: [], covers: [] } },
      },
    });
  }
  const edges = diagram.spec.edges.map((e): Edge => {
    const uses = e.kind !== 'next';
    return {
      ...(story ? { type: 'straight' } : {}),
      id: e.id,
      source: e.from,
      target: e.to,
      className: `edge-diagram edge-diagram-${e.kind}${story ? ' edge-story' : ''}${e.confidence === 'low' ? ' edge-uncertain' : ''}`,
      markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 14 },
      ...(e.label ? { label: e.label } : {}),
      ...(uses ? { style: { strokeDasharray: '4 4' } } : {}),
    };
  });
  return { nodes, edges };
}
